import { type ReactNode, useState } from "react";
import { WarningIcon } from "@phosphor-icons/react/Warning";
import { InfoIcon } from "@phosphor-icons/react/Info";
import { CheckCircleIcon } from "@phosphor-icons/react/CheckCircle";
import { XCircleIcon } from "@phosphor-icons/react/XCircle";
import { CaretDownIcon } from "@phosphor-icons/react/CaretDown";
import { CaretUpIcon } from "@phosphor-icons/react/CaretUp";
import { PushPinIcon } from "@phosphor-icons/react/PushPin";
import { Icon } from "../Icons/Icon";
import { BannerProvider } from "./Internal/BannerContext";
import { useLabelLayout } from "../FormLayout/Internal/FormItemContext";
import { headingFontStyle } from "../Theme/headingFontStyle";

type BannerVariant = "info" | "warning" | "success" | "danger" | "neutral";

// Styling: a distinct, structured callout — a solid accent rail down the
// left edge, a solid colour-filled icon medallion, and a softly tinted surface
// on the token palette. `bg`/`border` tint the card, `solid` is the vivid
// variant colour used for both the rail and the medallion, and `glyph` is the
// icon colour on that medallion — white on the dark indigo/red, but dark ink on
// the bright amber/green where white would wash out.
// `info` maps to the accent (indigo) so it harmonises with the rest of the
// the chrome; there is no success token, so success reuses the base green.
const variantStyleMap: Record<
  BannerVariant,
  { bg: string; border: string; solid: string; glyph: string }
> = {
  info: {
    bg: "bg-brand-subtle",
    border: "border-very-light-blue",
    solid: "bg-brand",
    glyph: "text-white",
  },
  warning: {
    bg: "bg-warning/10",
    border: "border-warning/30",
    solid: "bg-warning",
    glyph: "text-foreground",
  },
  success: {
    bg: "bg-green/10",
    border: "border-green/30",
    solid: "bg-green",
    glyph: "text-foreground",
  },
  danger: {
    bg: "bg-red/10",
    border: "border-red/30",
    solid: "bg-red",
    glyph: "text-white",
  },
  // A low-key grey callout for "important" notices that aren't info / success /
  // warning / error — e.g. requirements the customer must note. Grey surface +
  // rail, a mid-grey medallion, and a non-alarming pin icon (see variantIcon).
  neutral: {
    bg: "bg-muted",
    border: "border-border",
    solid: "bg-content-secondary",
    glyph: "text-white",
  },
};

const variantIcon: Record<BannerVariant, typeof InfoIcon> = {
  info: InfoIcon,
  warning: WarningIcon,
  success: CheckCircleIcon,
  danger: XCircleIcon,
  // "Important" — a pin, not a warning triangle.
  neutral: PushPinIcon,
};

interface BannerProps {
  title?: string | undefined;
  children: ReactNode;
  variant?: BannerVariant;
  collapsible?: boolean;
  defaultOpen?: boolean;
  actions?: ReactNode;
  /** Drop the icon medallion for a cleaner inline notice. The accent rail and
   *  tinted surface stay; only the medallion is removed. */
  hideIcon?: boolean;
}

export function Banner({
  title,
  children,
  variant = "info",
  collapsible = false,
  defaultOpen = true,
  actions,
  hideIcon = false,
}: BannerProps) {
  const [open, setOpen] = useState(defaultOpen);
  const baseStyles = variantStyleMap[variant];
  const VariantIcon = variantIcon[variant];
  const { labelPosition } = useLabelLayout();
  // When inside a beside-layout FormGroup grid, span both label + input columns
  const colSpan = labelPosition === "beside" ? "col-span-2" : "";

  const caret = collapsible ? (
    <button
      type="button"
      onClick={() => setOpen(!open)}
      className="text-faint transition-colors hover:text-muted-foreground"
    >
      {open ? <Icon icon={CaretUpIcon} /> : <Icon icon={CaretDownIcon} />}
    </button>
  ) : undefined;

  // a structured callout. A solid accent rail rides the left edge; the
  // icon sits in a solid colour-filled medallion aligned to the TITLE line (not
  // the block centre — that's what read as misaligned before). The body drops
  // below and indents to sit flush under the title.
  const medallion = (
    <span
      className={`flex size-8 shrink-0 items-center justify-center rounded-full shadow-sm ring-1 ring-black/5 ${baseStyles.solid}`}
    >
      <Icon
        icon={VariantIcon}
        size="md"
        weight="fill"
        color={baseStyles.glyph}
      />
    </span>
  );
  // Medallion is a real left column so it centres vertically against the
  // WHOLE content block (title + body), not just the title line — the balance
  // the "Edit General Info" warning has. No-title banners render the body as
  // the heading (bold, foreground), so a bare message reads with the same weight
  // as a title rather than as light secondary text.
  return (
    <BannerProvider value={true}>
      <div
        className={`relative flex items-center gap-3 overflow-hidden rounded-xl border ${baseStyles.border} ${baseStyles.bg} px-4 py-3 text-sm text-content ${colSpan}`}
      >
        {/* solid accent rail — clipped to the card's rounded corners */}
        <span
          className={`absolute inset-y-0 left-0 w-[3px] ${baseStyles.solid}`}
        />
        {!hideIcon && medallion}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {title ? (
            <>
              <div className="flex items-center justify-between gap-2">
                <span
                  className="text-base font-semibold text-foreground"
                  style={headingFontStyle()}
                >
                  {title}
                </span>
                {(actions || caret) && (
                  <div className="flex shrink-0 items-center gap-1">
                    {actions}
                    {caret}
                  </div>
                )}
              </div>
              {(!collapsible || open) && (
                <div className="flex flex-wrap items-center gap-1.5 leading-relaxed">
                  {children}
                </div>
              )}
            </>
          ) : (
            <div
              className="flex flex-wrap items-center gap-1.5 text-sm font-semibold text-foreground"
              style={headingFontStyle()}
            >
              {children}
            </div>
          )}
        </div>
      </div>
    </BannerProvider>
  );
}
